/**
 * Health: allergies and disabilities, each picked from the shared catalogue with an optional note
 * (severity, triggers, help needed). Self mode uses /me/allergies and /me/disabilities, admin mode
 * the /{memberId}/ routes.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconPlus, IconEdit, IconTrash, IconAlertCircle, IconFirstAidKit, IconAccessible } from '@tabler/icons-react';
import { Card, Stack, Group, Button, Select, Textarea, SimpleGrid, Text, Alert, Modal, Skeleton, Tooltip, ActionIcon, ThemeIcon, Badge } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import type { MemberTabProps } from '../types';
import { useAllergyCatalogue, useDisabilityCatalogue } from '../useMemberLookups';
import { errorMessage } from '../memberFormat';

type Kind = 'allergy' | 'disability';
type Editing = { kind: Kind; id: number | null; name?: string | null } | null;

interface HealthRow { id: number; name?: string | null; note?: string | null }

export function HealthTab({ api, perms }: MemberTabProps) {
  const queryClient = useQueryClient();
  const allergies = useQuery({ queryKey: api.keys.allergies, queryFn: () => api.allergies.list() });
  const disabilities = useQuery({ queryKey: api.keys.disabilities, queryFn: () => api.disabilities.list() });
  const [editing, setEditing] = useState<Editing>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const allergyCatalogue = useAllergyCatalogue(editing?.kind === 'allergy' || (allergies.data?.length ?? 0) > 0);
  const disabilityCatalogue = useDisabilityCatalogue(editing?.kind === 'disability');

  const severeIds = useMemo(() => new Set((allergyCatalogue.data ?? []).filter((a) => a.isSevere).map((a) => a.allergyID)), [allergyCatalogue.data]);

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const text = note.trim() || null;
      if (editing.kind === 'allergy') {
        if (editing.id) return api.allergies.update(editing.id, { note: text });
        if (!picked) throw new Error('Choose an allergy first.');
        return api.allergies.add({ allergyID: Number(picked), note: text });
      }
      if (editing.id) return api.disabilities.update(editing.id, { note: text });
      if (!picked) throw new Error('Choose a disability first.');
      return api.disabilities.add({ disabilityID: Number(picked), note: text });
    },
    onSuccess: () => {
      toast.success('Saved', editing?.kind === 'allergy' ? 'Allergy saved.' : 'Disability saved.');
      queryClient.invalidateQueries({ queryKey: editing?.kind === 'allergy' ? api.keys.allergies : api.keys.disabilities });
      setEditing(null);
    },
    onError: (e) => toast.error('Could not save', errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: ({ kind, id }: { kind: Kind; id: number }) => (kind === 'allergy' ? api.allergies.remove(id) : api.disabilities.remove(id)),
    onSuccess: (_d, v) => {
      toast.success('Removed', v.kind === 'allergy' ? 'Allergy removed.' : 'Disability removed.');
      queryClient.invalidateQueries({ queryKey: v.kind === 'allergy' ? api.keys.allergies : api.keys.disabilities });
    },
    onError: (e) => toast.error('Could not remove', errorMessage(e)),
  });

  const open = (kind: Kind, row?: HealthRow) => {
    setEditing({ kind, id: row?.id ?? null, name: row?.name });
    setPicked(row ? String(row.id) : null);
    setNote(row?.note ?? '');
    save.reset();
  };

  const allergyRows: HealthRow[] = (allergies.data ?? []).map((a) => ({ id: a.allergyID, name: a.name, note: a.note }));
  const disabilityRows: HealthRow[] = (disabilities.data ?? []).map((d) => ({ id: d.disabilityID, name: d.name, note: d.note }));

  const allergyOptions = (allergyCatalogue.data ?? [])
    .filter((a) => !allergyRows.some((r) => r.id === a.allergyID))
    .map((a) => ({ value: String(a.allergyID), label: a.isSevere ? `${a.name} (severe)` : a.name }));
  const disabilityOptions = (disabilityCatalogue.data ?? [])
    .filter((d) => !disabilityRows.some((r) => r.id === d.disabilityID))
    .map((d) => ({ value: String(d.disabilityID), label: d.name }));

  const section = (kind: Kind, rows: HealthRow[], loading: boolean, error: unknown) => {
    const isAllergy = kind === 'allergy';
    return (
      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" mb="sm">
          <Group gap="sm">
            <ThemeIcon variant="light" color={isAllergy ? 'red' : 'violet'} size="lg">
              {isAllergy ? <IconFirstAidKit size={18} /> : <IconAccessible size={18} />}
            </ThemeIcon>
            <Text fw={600}>{isAllergy ? 'Allergies' : 'Disabilities'}</Text>
          </Group>
          {perms.edit && <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => open(kind)}>Add</Button>}
        </Group>
        {loading && <Stack gap="xs"><Skeleton height={36} /><Skeleton height={36} /></Stack>}
        {!!error && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(error)}</Alert>}
        {!loading && !error && rows.length === 0 && (
          <Text size="sm" c="dimmed">{isAllergy ? 'No allergies recorded.' : 'No disabilities recorded.'}</Text>
        )}
        <Stack gap="sm">
          {rows.map((r) => (
            <Group key={r.id} justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                <Group gap={6}>
                  <Badge variant="light" color={isAllergy ? 'red' : 'violet'}>{r.name ?? (isAllergy ? `Allergy ${r.id}` : `Disability ${r.id}`)}</Badge>
                  {isAllergy && severeIds.has(r.id) && <Badge size="xs" color="red" variant="filled">Severe</Badge>}
                </Group>
                {r.note && <Text size="sm" c="dimmed">{r.note}</Text>}
              </Stack>
              {perms.edit && (
                <Group gap={4} wrap="nowrap">
                  <Tooltip label="Edit note"><ActionIcon variant="subtle" aria-label="Edit note" onClick={() => open(kind, r)}><IconEdit size={16} /></ActionIcon></Tooltip>
                  <Tooltip label="Remove">
                    <ActionIcon variant="subtle" color="red" aria-label="Remove"
                      onClick={() => modal.confirm({
                        title: `Remove ${r.name ?? (isAllergy ? 'this allergy' : 'this disability')}?`,
                        body: 'It will no longer show on this profile.',
                        confirmLabel: 'Remove',
                        confirmColor: 'red',
                        onConfirm: () => remove.mutate({ kind, id: r.id }),
                      })}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              )}
            </Group>
          ))}
        </Stack>
      </Card>
    );
  };

  return (
    <Stack gap="md">
      <Card withBorder padding="lg" radius="md">
        <Text fw={600}>Health</Text>
        <Text size="sm" c="dimmed">
          {api.mode === 'self'
            ? 'Allergies and disabilities help leaders care for you well at events, camps and kids church.'
            : 'Allergies and disabilities help leaders care well at events, camps and kids church. Treat them as confidential.'}
        </Text>
      </Card>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {section('allergy', allergyRows, allergies.isLoading, allergies.error)}
        {section('disability', disabilityRows, disabilities.isLoading, disabilities.error)}
      </SimpleGrid>

      <Modal opened={!!editing} onClose={() => setEditing(null)} centered size="md"
        title={editing?.id ? `Edit ${editing.name ?? (editing.kind === 'allergy' ? 'allergy' : 'disability')}` : editing?.kind === 'allergy' ? 'Add an allergy' : 'Add a disability'}>
        <Stack gap="md">
          {editing && !editing.id && (
            <Select
              label={editing.kind === 'allergy' ? 'Allergy' : 'Disability'}
              placeholder="Search the list"
              searchable
              required
              data={editing.kind === 'allergy' ? allergyOptions : disabilityOptions}
              value={picked}
              onChange={setPicked}
              nothingFoundMessage="Nothing matches"
            />
          )}
          <Textarea label="Note" placeholder="For example severity, triggers or help needed" autosize minRows={3} maxLength={500}
            value={note} onChange={(e) => setNote(e.currentTarget.value)} />
          {save.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(save.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>Cancel</Button>
            <Button loading={save.isPending} disabled={!!editing && !editing.id && !picked} onClick={() => save.mutate()}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
